/**
 * SieveSheetView — the branded AFS sieve worksheet (header band + editable table
 * + GFN footer), shared by the Data-entry capture form (editable) and the Reports
 * sheet (`readOnly`). Extracted from SieveReportPanel so capture + report render
 * an identical sheet from one component.
 *
 * Editable mode: `masses` (string per mesh, so a field can be blank) + `onMass`
 * drive the retained-mass inputs; `reportedGfn` + `onReportedGfn` the cross-check.
 * Read-only mode: the retained-mass column shows the computed `sheet` values and
 * the cross-check is static — used by the report, which reads stored readings.
 */

import { screenLabel, type SieveSheet } from "@/lib/qc/sieve-sheet";

export function SieveSheetView({
  sheet,
  masses,
  onMass,
  reportedGfn,
  onReportedGfn,
  orgName,
  logoUrl,
  subjectName,
  readOnly = false,
  showCrossCheck = true,
}: {
  sheet: SieveSheet;
  masses: Record<string, string>;
  onMass?: (mesh: string, value: string) => void;
  reportedGfn: string;
  onReportedGfn?: (value: string) => void;
  orgName: string;
  logoUrl: string | null;
  subjectName?: string;
  readOnly?: boolean;
  /** Report-only reported-fineness cross-check. Hidden in capture (not persisted in P1). */
  showCrossCheck?: boolean;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      {/* Branded header band */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-surface-elevated/40 px-4 py-3">
        <div className="flex items-center gap-3">
          {logoUrl && <img src={logoUrl} alt="" className="h-8 w-auto max-w-[120px] object-contain" />}
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-on-surface">{orgName}</span>
            <span className="text-[11px] text-on-surface-muted">
              AFS Grain Fineness Number — Sieve Analysis
              {subjectName ? ` · ${subjectName}` : ""}
            </span>
          </div>
        </div>
        <div className="flex flex-col items-end">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">GFN (AFS)</span>
          <span className="font-mono text-2xl font-semibold text-on-surface">{sheet.gfn.toFixed(2)}</span>
        </div>
      </div>

      {/* The sheet */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-surface-elevated/40 text-left text-xs text-on-surface-muted">
              <th className="px-3 py-2 font-medium">Mesh (ASTM)</th>
              <th className="px-3 py-2 font-medium">Mesh (BSS)</th>
              <th className="px-3 py-2 text-right font-medium">Screen (mm)</th>
              <th className="px-3 py-2 text-right font-medium">Retained (g)</th>
              <th className="px-3 py-2 text-right font-medium">Retained %</th>
              <th className="px-3 py-2 text-right font-medium">Cumulative %</th>
              <th className="px-3 py-2 text-right font-medium">Passing %</th>
              <th className="px-3 py-2 text-right font-medium" title="AFS grain-fineness multiplier">
                M3
              </th>
              <th className="px-3 py-2 text-right font-medium" title="Retained % × multiplier">
                R.M3
              </th>
            </tr>
          </thead>
          <tbody>
            {sheet.rows.map((r) => (
              <tr key={r.meshDesignation} className="border-b border-border/60 last:border-0">
                <td className="px-3 py-1.5 font-medium text-on-surface">{r.astm}</td>
                <td className="px-3 py-1.5 text-on-surface-muted">{r.bss}</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-on-surface-muted">{screenLabel(r)}</td>
                <td className="px-2 py-1 text-right">
                  {readOnly ? (
                    <span className="inline-block w-20 pr-1.5 text-right text-xs tabular-nums text-on-surface">
                      {r.retainedMass}
                    </span>
                  ) : (
                    <input
                      type="number"
                      inputMode="decimal"
                      min={0}
                      step="any"
                      value={masses[r.meshDesignation] ?? ""}
                      onChange={(e) => onMass?.(r.meshDesignation, e.target.value)}
                      placeholder="0"
                      aria-label={`Mass retained on mesh ${r.astm}`}
                      className="h-7 w-20 rounded border border-border bg-surface px-1.5 text-right text-xs tabular-nums text-on-surface focus:border-brand focus:outline-none"
                    />
                  )}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums">{r.retainedPct.toFixed(2)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-on-surface-muted">
                  {r.cumulativePct.toFixed(2)}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums text-on-surface-muted">
                  {r.passingPct.toFixed(2)}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums text-on-surface-muted">{r.multiplier}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{r.product.toFixed(2)}</td>
              </tr>
            ))}
            {/* Totals */}
            <tr className="border-t-2 border-border bg-surface-elevated/40 font-medium">
              <td className="px-3 py-2" colSpan={3}>
                Total
              </td>
              <td className="px-3 py-2 text-right tabular-nums">{sheet.totalMass}</td>
              <td className="px-3 py-2 text-right tabular-nums">{sheet.sumRetainedPct.toFixed(2)}</td>
              <td className="px-3 py-2" colSpan={3} />
              <td className="px-3 py-2 text-right tabular-nums">{sheet.sumProduct.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* GFN footer + reported cross-check */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-surface-elevated/20 px-4 py-3 text-xs">
        <span className="text-on-surface-muted">
          GFN = Σ(R.M3) / Σ(% retained) ={" "}
          <span className="font-mono font-semibold text-on-surface">
            {sheet.sumProduct.toFixed(2)} / {sheet.sumRetainedPct.toFixed(2)} = {sheet.gfn.toFixed(2)}
          </span>
        </span>
        {showCrossCheck && (
        <label className="flex items-center gap-2 text-on-surface-muted">
          Reported fineness (cross-check)
          {readOnly ? (
            <span className="inline-block w-20 pr-1.5 text-right text-xs tabular-nums text-on-surface">
              {reportedGfn || "—"}
            </span>
          ) : (
            <input
              type="number"
              inputMode="decimal"
              step="any"
              value={reportedGfn}
              onChange={(e) => onReportedGfn?.(e.target.value)}
              placeholder="—"
              aria-label="Reported fineness number"
              className="h-7 w-20 rounded border border-border bg-surface px-1.5 text-right text-xs tabular-nums text-on-surface focus:border-brand focus:outline-none"
            />
          )}
        </label>
        )}
      </div>
    </div>
  );
}
